import React from 'react';
import { Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    // Added pb-20 for bottom spacing
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 p-4 md:p-8 text-gray-800 dark:text-gray-200 pb-20">
      <div className="max-w-4xl mx-auto">
        
        {/* --- HEADER SECTION --- */}
        <div className="text-center mb-10 pt-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 mb-4">
            <Shield size={32} className="text-purple-600 dark:text-purple-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
            Last updated: September 26, 2026
          </p>
        </div>

        {/* --- CONTENT CARD --- */}
        <div className="bg-white dark:bg-gray-800 p-6 md:p-10 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div 
            className="prose dark:prose-invert max-w-none" 
            dangerouslySetInnerHTML={{ __html: sanitizedHTML }} 
          />
          <style>{`
            .prose h2 { font-size: 1.25rem; font-weight: 600; margin-top: 1.75rem; margin-bottom: 0.75rem; color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.25rem; }
            .dark .prose h2 { color: #e5e7eb; border-color: #374151; }
            
            .prose h3 { font-size: 1.1rem; font-weight: 600; margin-top: 1.5rem; color: #374151; }
            .dark .prose h3 { color: #d1d5db; }

            .prose p, .prose li { font-size: 0.95rem; line-height: 1.7; color: #4b5563; }
            .dark .prose p, .dark .prose li { color: #d1d5db; }

            .prose a { color: #2563eb; text-decoration: underline; font-weight: 500; }
            .prose a:hover { color: #1d4ed8; }
            
            .prose ul { list-style-type: disc; padding-left: 1.5rem; margin-top: 0.5rem; margin-bottom: 0.5rem; }
            .prose li { margin-bottom: 0.5rem; }
          `}</style>
        </div>
      </div>
    </div>
  );
}

const sanitizedHTML = `
  <div>
    <p>
      Your privacy is important to us. This Privacy Policy explains how Edge Hub collects, uses, and protects your information when you use our platform.
    </p>

    <h2>1. About Us</h2>
    <p>
      Edge Hub is a SaaS platform that provides tools designed to help traders analyze and improve their trading behavior and performance. We are registered and operate under the laws of the Federal Republic of Nigeria.
    </p>

    <h2>2. Information We Collect</h2>
    <p>When you use Edge Hub, we collect the following categories of information:</p>
    
    <h3>2.1 Personal Information</h3>
    <ul>
      <li><strong>Identity Data:</strong> First name, last name, and username.</li>
      <li><strong>Contact Data:</strong> Email address and phone number (if provided).</li>
      <li><strong>Technical Data:</strong> Browser type, approximate network information, operating system, security logs, and session identifiers. We do not use this list to promise collection of data that the deployed service does not actually receive.</li>
    </ul>

    <h3>2.2 Financial & Trading Data</h3>
    <p>To provide our core services, we collect specific data related to your trading activities:</p>
    <ul>
      <li><strong>Broker Credentials:</strong> MT5 account numbers, passwords, and server names provided by you to connect your account. Credentials are encrypted in the application database and used by the MT5 worker. The connection is not promised to be read-only: an active psychology safety lock may instruct the worker to close open positions.</li>
      <li><strong>Trading History:</strong> Open and closed trades, balance, equity, drawdown, and profit/loss figures.</li>
      <li><strong>Performance Metrics:</strong> Calculated win rates, risk-to-reward ratios, and psychology scores derived from your trading patterns.</li>
      <li><strong>Safety Events:</strong> Rule indicators, strikes, lock status, lock expiry, and related audit notifications used to enforce the trading safety feature.</li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <p>We use the information we collect to:</p>
    <ul>
      <li>Create and manage user accounts.</li>
      <li>Provide and operate the Edge Hub platform, specifically to synchronize and analyze your trading data.</li>
      <li>Generate psychology-based insights and performance metrics.</li>
      <li>Communicate updates, notifications, and support messages.</li>
      <li>Maintain the security and integrity of the platform.</li>
      <li>Apply server-authoritative trading safety locks when repeated high-severity rule evidence meets the published threshold.</li>
      <li>Comply with legal obligations.</li>
    </ul>

    <h2>4. Data Sharing & Sub-Processors</h2>
    <p>Edge Hub does not sell, rent, or trade your personal information to third parties. We may share information only when:</p>
    <ul>
      <li><strong>Service Providers:</strong> We use hosting and infrastructure providers such as Render and Cloudflare. If enabled, the Markets beta requests data from World News API and FRED; those providers receive the request needed to return their data and have their own terms and privacy policies. We do not send your MT5 password to these market-data providers.</li>
      <li><strong>Legal Requirement:</strong> Required by law or to protect the rights/safety of the platform.</li>
    </ul>

    <h2>5. Data Security</h2>
    <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
    <ul>
      <li>Encryption of data in transit (SSL/TLS) and at rest.</li>
      <li>Secure hashing of sensitive credentials.</li>
      <li>Regular security monitoring and access controls.</li>
    </ul>
    <p>However, no internet transmission or electronic storage method is completely secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.</p>

    <h2>6. Data Retention</h2>
    <p>We retain personal information only for as long as necessary to provide our services and fulfill legal obligations. Specifically:</p>
    <ul>
      <li><strong>Active Accounts:</strong> Data is retained as long as your account is active.</li>
      <li><strong>Terminated Accounts:</strong> Upon account deletion request, we will delete or anonymize your data within 30 days, except where retention is required for legal compliance.</li>
    </ul>

    <h2>7. Your Rights</h2>
    <p>Depending on your location, you have the right to:</p>
    <ul>
      <li>Access the personal data we hold about you.</li>
      <li>Request corrections to inaccurate information.</li>
      <li>Request deletion of your data.</li>
      <li>Object to or restrict certain types of data processing.</li>
    </ul>
    <p>To exercise these rights, please contact us at <a href="mailto:edgehubfx@gmail.com">edgehubfx@gmail.com</a>.</p>

    <h2>8. Children's Privacy</h2>
    <p>Our Service is intended for users who are at least 18 years old. We do not knowingly collect personal information from children under 18. If we become aware that a child under 18 has provided us with Personal Data, we will delete it immediately.</p>

    <h2>9. Changes to This Policy</h2>
    <p>Edge Hub may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. Continued use of the platform means you accept the updated policy.</p>

    <h2>10. Cookies, Local Storage & Similar Technologies</h2>
    <p>We use strictly necessary browser storage for authentication, preferences, the selected Markets watchlist, and security. The Markets watchlist is stored locally in your browser. We do not describe this as advertising tracking. If optional analytics, advertising, or support cookies are introduced, we will identify them and request consent where required by law.</p>

    <h2>11. Contact Us</h2>
    <p>If you have any questions about this Privacy Policy, please contact us:</p>
    <p>
      <strong>EDGE HUB</strong><br/>
      Nnewi-Nnobi Rd, Nnewi, Anambra 432105, Nigeria<br/>
      Email: <a href="mailto:edgehubfx@gmail.com">edgehubfx@gmail.com</a>
    </p>
  </div>
`;
